import React, { useState, useRef, useCallback } from 'react';
import { FaCamera, FaImage } from 'react-icons/fa';

const CameraApp: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>(''); // Type for selected filter
  const [imageSrc, setImageSrc] = useState<string | null>(null); // Type for image source
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false); // State for camera activity
  const [isProcessing, setIsProcessing] = useState<boolean>(false); // New state for processing status
  const [intensityLevel, setIntensityLevel] = useState<number>(1); // 1: Low, 2: Moderate, 3: High intensity
  const videoRef = useRef<HTMLVideoElement | null>(null); // Typing for videoRef
  const canvasRef = useRef<HTMLCanvasElement | null>(null); // Typing for canvasRef
  const streamRef = useRef<MediaStream | null>(null); // To store the stream for stopping later

  // Throttling function for slider change to avoid jerking issues
  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value);
      setIntensityLevel(value);
    },
    []
  );

  // Start the camera
  const startCamera = async () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } } // Force back camera (environment)
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream; // Attach the stream to the video element
          streamRef.current = stream; // Store the stream to stop later
          setIsCameraActive(true); // Set camera active to true
          console.log('Camera stream started');
        }
      } catch (err) {
        console.error('Error accessing the camera: ', err);
      }
    }
  };

  // Stop the camera
  const stopCamera = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop()); // Stop the stream when done
      if (videoRef.current) {
        videoRef.current.srcObject = null; // Disconnect the video stream
      }
    }
    setIsCameraActive(false); // Set camera active to false
    console.log('Camera stream stopped');
  };

  // Capture the image from video
  const captureImage = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (videoRef.current && context && canvas) {
      // Set canvas size to video dimensions
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;

      // Draw the current video frame onto the canvas
      context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      // Convert canvas to image URL and set state to display it
      const imageUrl = canvas.toDataURL('image/png');
      setImageSrc(imageUrl);
      stopCamera(); // Stop the camera after capture
    }
  };

  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const filter = e.target.value;
    setSelectedFilter(filter);
    setIntensityLevel(1); // Reset intensity to low (1) when filter changes
  };

  // Handle gallery select
  const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result as string); // Set the image from gallery
      };
      reader.readAsDataURL(file);
    }
  };

  // Process the image with the selected filter and intensity
  const processImage = async () => {
    setIsProcessing(true); // Set processing to true

    try {
      const canvas = canvasRef.current;
      if (!canvas || !imageSrc) {
        throw new Error('No image found.');
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        // Set canvas size to the image size
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Calculate intensity multiplier based on the intensity level
        const intensityMultiplier = intensityLevel === 1 ? 0.2 : intensityLevel === 2 ? 0.5 : 1;

        // Apply the selected filter logic
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]; // Red
          const g = data[i + 1]; // Green
          const b = data[i + 2]; // Blue

          if (selectedFilter === 'red') {
            // Split the red value to green and blue
            const splitValue = (r) * intensityMultiplier;
            data[i + 1] += splitValue / 2; // Add to green
            data[i + 2] += splitValue / 2; // Add to blue
            data[i] = r; // Keep red as is
          } else if (selectedFilter === 'green') {
            // Split the green value to red and blue
            const splitValue = (g) * intensityMultiplier;
            data[i] += splitValue / 2; // Add to red
            data[i + 2] += splitValue / 2; // Add to blue
            data[i + 1] = g; // Keep green as is
          } else if (selectedFilter === 'blue') {
            // Split the blue value to red and green
            const splitValue = (b) * intensityMultiplier;
            data[i] += splitValue / 2; // Add to red
            data[i + 1] += splitValue / 2; // Add to green
            data[i + 2] = b; // Keep blue as is
          }
        }

        // Update the canvas with modified image data
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to image URL and set state to display it
        const processedImageUrl = canvas.toDataURL('image/png');
        setImageSrc(processedImageUrl);
        setIsProcessing(false); // Set processing to false when done
      };
    } catch (error) {
      console.error('Error processing image:', error);
      setIsProcessing(false); // Set processing to false in case of error
    }
  };

  return (
    <div className="cameraApp">
      <h1>Select CVD Type</h1>

      {/* Dropdown for filter selection */}
      <select value={selectedFilter} onChange={handleFilterChange} className="filterDropdown">
        <option value="">-CVD-</option>
        <option value="red">Protanopia (Red)</option>
        <option value="green">Deuteranopia (Green)</option>
        <option value="blue">Tritanopia (Blue)</option>
      </select>

      {/* Intensity Slider */}
      {selectedFilter && (
        <div className="intensitySlider" style={{ marginTop: '20px', marginBottom: '20px' }}>
          <label style={{ marginRight: '10px' }}>Intensity: </label>
          <input
            type="range"
            min="1"
            max="3"
            value={intensityLevel}
            onChange={handleSliderChange}
            className="slider"
            style={{
              width: '80%',
              margin: '0 10px',
              height: '10px',
              borderRadius: '5px',
              background: 'linear-gradient(90deg, #FF0000, #00FF00, #0000FF)',
            }}
          />
          <span>
            {intensityLevel === 1 ? 'Low' : intensityLevel === 2 ? 'Moderate' : 'High'}
          </span>
        </div>
      )}

      {/* Display buttons if a filter is selected */}
      {selectedFilter && (
        <div className="buttons">
          <button className="launchButton" onClick={startCamera}>
            <FaCamera /> Launch Camera
          </button>
          <input
            type="file"
            accept="image/*"
            onChange={handleGallerySelect}
            style={{ display: 'none' }}
            id="galleryInput"
          />
          <button
            className="galleryButton"
            onClick={() => document.getElementById('galleryInput')?.click()}
          >
            <FaImage /> Select from Gallery
          </button>
        </div>
      )}

      {/* Video container */}
      <div
        className="cameraPreview"
        style={{
          display: isCameraActive ? 'flex' : 'none',
          position: 'relative',
          width: '100%',
          maxWidth: '600px',
          flexDirection: 'column',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
          }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }}></canvas> {/* Hidden canvas */}
        <button className="captureButton" onClick={captureImage}>
          Capture
        </button>
      </div>

      {/* Display the captured image or processed image */}
      {imageSrc && (
        <div className="imagePreview">
          <img
            src={imageSrc}
            alt="Captured or Processed"
            style={{
              maxWidth: '500px',
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}

      {/* Process image button */}
      {imageSrc && selectedFilter && (
        <button
          className="processButton"
          onClick={processImage}
          disabled={isProcessing}
          style={{
            backgroundColor: isProcessing ? '#cccccc' : '#4CAF50',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '5px',
            border: 'none',
            fontSize: '16px',
            marginTop: '20px',
          }}
        >
          {isProcessing ? 'Processing...' : 'Process Image'}
        </button>
      )}
    </div>
  );
};

export default CameraApp;
